import { lazy, Suspense, type ReactNode } from 'react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { useQuery } from '@tanstack/react-query';
import { useAutoSignIn, signOut, chooseAccount as startAccountPicker, type AutoSignInBlock } from '@/auth/autoSignIn';
import { devMode, devUser } from '@/auth/devMode';
import { useApi } from '@/api/client';
import type { Me } from '@/types';
import { AppBar } from '@/components/AppBar';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { MySubscriptions } from '@/pages/MySubscriptions';
import { SubscriptionDetailPage } from '@/pages/SubscriptionDetail';
import { CreateSubscription } from '@/pages/CreateSubscription';
import { RunHistory } from '@/pages/RunHistory';
import { Admin } from '@/pages/Admin';
import { Outbox } from '@/pages/Outbox';
import { KtInsights } from '@/pages/KtInsights';
import { KtInsightsHistory } from '@/pages/KtInsightsHistory';
import { ReportPickup } from '@/pages/ReportPickup';
import { CustomViews } from '@/pages/CustomViews';
import { CustomViewBuilder } from '@/pages/CustomViewBuilder';
import { ReportPacks } from '@/pages/ReportPacks';
import { ReportPackEditor } from '@/pages/ReportPackEditor';
import { ReportPackApply } from '@/pages/ReportPackApply';

// Help is loaded on first visit: its words and its search index are the size of a page of the app
// in their own right, and most sessions never open it.
const Help = lazy(() => import('@/pages/Help').then(m => ({ default: m.Help })));
const HelpGuide = lazy(() => import('@/pages/HelpGuide').then(m => ({ default: m.HelpGuide })));

export default function App() {
  if (devMode) return <AuthedApp />;
  return <RealAuthGate />;
}

function RealAuthGate() {
  const { authenticated, blocked, signIn, chooseAccount } = useAutoSignIn();

  if (authenticated) return <AuthedApp />;
  if (blocked) return <SignInCard blocked={blocked} onSignIn={signIn} onChooseAccount={chooseAccount} />;

  // Everything else — MSAL settling, silent SSO in flight, redirect about to
  // fire — is the happy path and lasts a few hundred milliseconds. Flashing a
  // Sign in button nobody gets to press reads as a broken page, so show a
  // quiet placeholder instead.
  return (
    <div className="signin-card signin-card-quiet" role="status">
      <div className="signin-spinner" aria-hidden="true" />
      <p>Signing you in&hellip;</p>
    </div>
  );
}

function SignInCard({
  blocked,
  onSignIn,
  onChooseAccount
}: {
  blocked: AutoSignInBlock;
  onSignIn: () => void;
  onChooseAccount: () => void;
}) {
  const deliberate = blocked.reason === 'signedOut';
  return (
    <div className="signin-card">
      <h2>{deliberate ? 'You are signed out' : 'We could not sign you in'}</h2>
      <p>
        {deliberate
          ? 'Sign back in with your Key Travel account to manage your scheduled reports.'
          : 'Automatic sign-in did not complete, so we stopped rather than sending you back to Entra again.'}
      </p>
      {blocked.message && <div className="error-banner signin-detail">{blocked.message}</div>}
      <div className="signin-actions">
        <button className="btn-primary" onClick={onSignIn}>Sign in</button>
        <button className="btn-secondary" onClick={onChooseAccount}>Use a different account</button>
      </div>
    </div>
  );
}

function AuthedApp() {
  const api = useApi();
  const { data: me, isPending, error, refetch } = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    // Dev mode renders straight away from a known identity — but as a PLACEHOLDER, not as
    // initialData, because the two behave very differently here and the difference was a bug.
    //
    // initialData is written into the cache and counts as a real fetch, so the other pages that
    // read ['me'] with `staleTime: Infinity` found it fresh and nothing ever called /api/me. Only
    // the identity was seeded, so that was invisible for as long as identity was all this carried
    // — and then `features` arrived, which ONLY the API can answer, and the whole feature silently
    // failed to appear in dev. placeholderData renders the same thing without pretending it came
    // from the server, so the fetch still happens and the real answer replaces it.
    placeholderData: devMode ? devUser : undefined
  });

  // isPending rather than isLoading: it stays true through the retry backoff,
  // where isLoading briefly drops to false with neither data nor error yet and
  // flashes the failure card at someone whose request is still in flight.
  if (isPending) return <div className="placeholder">Loading...</div>;
  if (error || !me) {
    // Rendered outside the AppBar, so without these actions there is no way out
    // of a failed profile load except closing the tab.
    return (
      <div className="signin-card">
        <h2>We could not load your profile</h2>
        <p>{(error as Error)?.message ?? 'The portal API did not return your details.'}</p>
        <div className="signin-actions">
          <button className="btn-primary" onClick={() => void refetch()}>Try again</button>
          <AccountEscapeActions />
        </div>
      </div>
    );
  }
  if (!me.isAdmin && !me.isAccountManager) return <NoAccessCard me={me} />;

  return (
    <>
      {devMode && <DevModeBanner />}
      <AppBar me={me} />
      <main>
        {/* Above the routes rather than inside each page: a new nested route
            gets its trail from the nav model without having to ask. */}
        <Breadcrumbs />
        <Routes>
          <Route path="/" element={<Navigate to="/subscriptions" replace />} />
          <Route path="/subscriptions" element={<MySubscriptions />} />
          <Route path="/subscriptions/new" element={<CreateSubscription />} />
          <Route path="/subscriptions/:id" element={<SubscriptionDetailPage />} />
          <Route path="/history" element={<RunHistory />} />
          <Route path="/pickup" element={<ReportPickup />} />
          <Route path="/ktinsights" element={<KtInsights />} />
          <Route path="/ktinsights/history" element={<KtInsightsHistory />} />
          <Route path="/help" element={<Lazy><Help /></Lazy>} />
          <Route path="/help/:slug" element={<Lazy><HelpGuide /></Lazy>} />
          {devMode && <Route path="/outbox" element={<Outbox />} />}
          {/* Declared before /admin/* only for readability — React Router ranks a static segment
              above a splat, so these win over the Admin catch-all wherever they are written. With
              the flag off they do not exist at all and /admin/packs falls through to Admin, which
              renders the ordinary admin page rather than anything pack-shaped. */}
          {me.isAdmin && me.features?.reportPacks && (
            <Route path="/admin/packs" element={<ReportPacks />} />
          )}
          {me.isAdmin && me.features?.reportPacks && (
            <Route path="/admin/packs/new" element={<ReportPackEditor />} />
          )}
          {me.isAdmin && me.features?.reportPacks && (
            <Route path="/admin/packs/:packId" element={<ReportPackEditor />} />
          )}
          {me.isAdmin && me.features?.reportPacks && (
            <Route path="/admin/packs/:packId/apply" element={<ReportPackApply />} />
          )}
          {me.isAdmin && me.features?.customViews && (
            <Route path="/admin/custom-views" element={<CustomViews />} />
          )}
          {me.isAdmin && me.features?.customViews && (
            <Route path="/admin/custom-views/new" element={<CustomViewBuilder />} />
          )}
          {me.isAdmin && me.features?.customViews && (
            <Route path="/admin/custom-views/:viewId/edit" element={<CustomViewBuilder />} />
          )}
          {me.isAdmin && <Route path="/admin/*" element={<Admin />} />}
          <Route path="*" element={<Navigate to="/subscriptions" replace />} />
        </Routes>
      </main>
    </>
  );
}

/**
 * Entra authenticated them but neither portal group did, so every endpoint
 * behind the AccountManager policy will 403. Auto sign-in makes this the one
 * state a user can reach repeatedly without ever having clicked anything, so
 * it has to be a dead end with a way out rather than a wall of failed requests.
 */
function NoAccessCard({ me }: { me: Me }) {
  return (
    <div className="signin-card">
      <h2>You do not have access yet</h2>
      <p>
        You are signed in as <strong>{me.email || me.displayName}</strong>, but that account is not a
        member of <code>SG-ReportPortal-AccountManagers</code> or <code>SG-ReportPortal-Admins</code>.
        Ask IT to add you, then sign in again.
      </p>
      <div className="signin-actions">
        <AccountEscapeActions />
      </div>
    </div>
  );
}

/**
 * The way out of any signed-in-but-stuck state. Rendered only outside dev mode,
 * where there is no MSAL instance to talk to.
 */
function AccountEscapeActions() {
  if (devMode) return null;
  return <MsalEscapeActions />;
}

function MsalEscapeActions() {
  const { instance } = useMsal();
  return (
    <>
      <button className="btn-secondary" onClick={() => void startAccountPicker(instance)}>
        Use a different account
      </button>
      <button className="btn-secondary" onClick={() => signOut(instance)}>Sign out</button>
    </>
  );
}

function Lazy({ children }: { children: ReactNode }) {
  return <Suspense fallback={<div className="placeholder">Loading...</div>}>{children}</Suspense>;
}

function DevModeBanner() {
  return (
    <div style={{
      background: 'var(--kt-orange-bg)',
      // Was the literal #f5cba8, which is the light theme's peach and stayed peach
      // on a dark page — a bright hairline under a deep amber banner.
      borderBottom: '1px solid var(--kt-orange-border)',
      padding: '8px 24px',
      textAlign: 'center',
      fontSize: '.82rem',
      color: 'var(--kt-grey-700)'
    }}>
      <strong style={{ color: 'var(--kt-orange)' }}>Dev mode</strong>
      &nbsp;&middot;&nbsp; auth bypassed &middot; catalogue live from the report server (sample fallback) &middot; schedules run in a local simulator, nothing touches the real report server
    </div>
  );
}
